import type { ReactElement } from 'react';
import { PolymorphicButtonBase, type PolymorphicButtonBaseProps } from '../primitives/button.tsx';
import { toolbarButtonVariants, type ToolbarButtonVariantProps } from '../primitives/toolbar-button.tsx';
import { cn } from '../util/classes.ts';

/**
 * Property type for {@link ToolbarIconButton}.
 */
export type ToolbarIconButtonProps = ToolbarButtonVariantProps &
    PolymorphicButtonBaseProps & {
        /** Names the action for assistive technology, since no text is visible. */
        label: string;
    };

/**
 * A toolbar button whose only content is an icon: the same variants and joined
 * behaviour as `ToolbarButton`, squared off to the interactive size with no
 * room for a label.
 *
 * ```tsx
 * <Toolbar>
 *     <ToolbarIconButton label="Bold">
 *         <BoldIcon />
 *     </ToolbarIconButton>
 * </Toolbar>
 * ```
 */
export function ToolbarIconButton({
    className,
    label,
    variant,
    ...props
}: ToolbarIconButtonProps): ReactElement {
    return (
        <PolymorphicButtonBase
            aria-label={label}
            className={cn(
                toolbarButtonVariants({ variant }),
                'px-0 l2l3:px-0',
                "*:[svg:not([class*='size-'])]:size-4",
                className,
            )}
            {...props}
        />
    );
}
